/**
 * Client-side contact validation — mirrors the API validation rules.
 *
 * Validates scalar contacts (email, phone, skype, etc.) and address contacts
 * with TCSI rules for Australian addresses (special chars, max length, PO BOX).
 */

// ============================================================================
// Patterns (must match API validation.ts)
// ============================================================================

const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
const E164_REGEX = /^\+[1-9]\d{5,14}$/;
const SKYPE_REGEX = /^[a-zA-Z][a-zA-Z0-9._-]{5,31}$/;
const TCSI_ALLOWED_CHARS_REGEX = /^[a-zA-Z0-9 &'(),\-./[\]_:#]*$/;
const TCSI_MAX_FIELD_LENGTH = 255;
const PO_BOX_REGEX = /POBOX |PO BOX |GPOBOX |GPO BOX /i;

// ============================================================================
// Type groupings
// ============================================================================

const ADDRESS_TYPE_IDS = new Set([6, 13, 17, 18]);
const EMAIL_TYPE_IDS = new Set([1, 14]);
const PHONE_TYPE_IDS = new Set([2, 3, 4, 5, 15, 16]);
/** TCSI-reportable address types: Study (6), Home (17), Postal (18) — NOT Work (13) */
const TCSI_REPORTABLE_TYPE_IDS = new Set([6, 17, 18]);

// ============================================================================
// Validation
// ============================================================================

export interface ContactFormValues {
  typeId: number;
  val: string;
  building: string;
  unit: string;
  streetNumber: string;
  streetName: string;
  locality: string;
  region: string;
  postcode: string;
  country: string;
}

/**
 * Validate contact form values before submission.
 * Returns an array of error strings (empty = valid).
 */
export function validateContactForm(form: ContactFormValues): string[] {
  const errors: string[] = [];

  if (ADDRESS_TYPE_IDS.has(form.typeId)) {
    validateAddress(form, errors);
  } else if (EMAIL_TYPE_IDS.has(form.typeId)) {
    if (!form.val.trim()) {
      errors.push('Email address is required');
    } else if (!EMAIL_REGEX.test(form.val.trim())) {
      errors.push('Invalid email format');
    }
  } else if (PHONE_TYPE_IDS.has(form.typeId)) {
    if (!form.val.trim()) {
      errors.push('Phone number is required');
    } else if (!E164_REGEX.test(form.val.trim())) {
      errors.push('Must be E.164 format (e.g. +61412345678)');
    }
  } else if (form.typeId === 7) {
    // Skype
    if (!form.val.trim()) {
      errors.push('Skype username is required');
    } else if (!SKYPE_REGEX.test(form.val.trim())) {
      errors.push('Invalid Skype username (start with letter, 6-32 chars, alphanumeric/dots/underscores/hyphens)');
    }
  } else {
    // Free text types
    if (!form.val.trim()) {
      errors.push('Value is required');
    }
  }

  return errors;
}

function validateAddress(form: ContactFormValues, errors: string[]): void {
  if (!form.country.trim()) {
    errors.push('Country is required');
  }

  const isAustralian = form.country.trim().toUpperCase() === 'AUSTRALIA';
  const isTcsiReportable = TCSI_REPORTABLE_TYPE_IDS.has(form.typeId);

  if (isAustralian && isTcsiReportable) {
    if (!form.locality.trim()) errors.push('Locality is required for Australian addresses');
    if (!form.region.trim()) errors.push('Region is required for Australian addresses');
    if (!form.postcode.trim()) errors.push('Postcode is required for Australian addresses');

    // TCSI character and length checks on all address fields
    const fields: Array<[string, string]> = [
      ['Building', form.building],
      ['Unit', form.unit],
      ['Street Number', form.streetNumber],
      ['Street Name', form.streetName],
      ['Locality', form.locality],
      ['Region', form.region],
      ['Postcode', form.postcode],
    ];

    for (const [label, value] of fields) {
      if (!value.trim()) continue;
      if (value.trim().length > TCSI_MAX_FIELD_LENGTH) {
        errors.push(`${label} must not exceed ${TCSI_MAX_FIELD_LENGTH} characters`);
      }
      if (!TCSI_ALLOWED_CHARS_REGEX.test(value.trim())) {
        errors.push(`${label} contains characters not allowed for TCSI reporting`);
      }
    }

    // PO BOX forbidden for Study Address (6) and Home Address (17)
    if (form.typeId === 6 || form.typeId === 17) {
      for (const [label, value] of fields) {
        if (value && PO_BOX_REGEX.test(value)) {
          errors.push(`${label} must not contain a PO BOX for this address type`);
        }
      }
    }
  }
}
